import React, { useState, useMemo } from 'react';
import './KPIDetailsModal.css';

interface KPIData {
  category: string;
  name: string;
  description: string;
  value: string | number | null;  // null means dealer isn't reporting this KPI/period type
  type?: string;
  abbreviation?: string;
  typeDescription?: string;
}

interface CustomImportSettings {
  enabled: boolean;
  periodTypes?: string[];
  displayConfig?: {
    showPeriodSelector?: boolean;
    defaultPeriodType?: string;
    periodLabels?: Record<string, string>;
  };
}

interface KPIDetailsModalProps {
  isOpen: boolean;
  onClose: () => void;
  kpiData: KPIData[];
  loading?: boolean;
  customImportSettings?: CustomImportSettings | null;
  submissionInfo: {
    location: string;
    period: string;
    submittedDate: string;
    periodTypes?: string[];
  };
}

const KPIDetailsModal: React.FC<KPIDetailsModalProps> = ({
  isOpen,
  onClose,
  kpiData,
  loading = false,
  customImportSettings,
  submissionInfo
}) => {
  // Period type selector state
  const [selectedPeriodType, setSelectedPeriodType] = useState<string>(
    customImportSettings?.displayConfig?.defaultPeriodType || 'MTD'
  );

  // Get available period types from the data - must be called before early return
  const availablePeriodTypes = useMemo(() => {
    if (!kpiData || kpiData.length === 0) return [];
    const types = new Set(kpiData.map(kpi => kpi.abbreviation || 'MTD').filter(Boolean));
    return Array.from(types);
  }, [kpiData]);

  // Filter KPIs by selected period type (for custom import orgs) - must be called before early return
  const filteredKpiData = useMemo(() => {
    if (!customImportSettings?.enabled || availablePeriodTypes.length <= 1) {
      return kpiData;
    }
    return kpiData.filter(kpi => (kpi.abbreviation || 'MTD') === selectedPeriodType);
  }, [kpiData, customImportSettings, selectedPeriodType, availablePeriodTypes]);

  // Group KPIs by category - must be called before early return
  const groupedKPIs = useMemo(() => {
    return filteredKpiData.reduce((groups, kpi) => {
      const category = kpi.category || 'Uncategorized';
      if (!groups[category]) {
        groups[category] = [];
      }
      groups[category].push(kpi);
      return groups;
    }, {} as Record<string, KPIData[]>);
  }, [filteredKpiData]);
  
  // Early return after all hooks
  if (!isOpen) return null;
  
  // Get period label
  const getPeriodLabel = (abbrev: string) => {
    return customImportSettings?.displayConfig?.periodLabels?.[abbrev] || abbrev;
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div className="kpi-modal-overlay" onClick={handleBackdropClick}>
      <div className="kpi-modal-container">
        <div className="kpi-modal-header">
          <div className="kpi-modal-title">
            <h2>KPI Details</h2>
            <div className="kpi-modal-subtitle">
              <span className="submission-location">{submissionInfo.location}</span>
              <span className="submission-period">{submissionInfo.period}</span>
              <span className="submission-date">Submitted: {submissionInfo.submittedDate}</span>
            </div>
          </div>
          <button 
            className="kpi-modal-close" 
            onClick={onClose}
            aria-label="Close modal"
          >
            ×
          </button>
        </div>

        <div className="kpi-modal-content">
          {loading ? (
            <div className="kpi-modal-loading">
              <div className="loading-spinner"></div>
              <p>Loading KPI details...</p>
            </div>
          ) : (
            <>
              <div className="kpi-summary">
                <span className="kpi-count">{filteredKpiData.length} KPIs</span>
                <span className="category-count">{Object.keys(groupedKPIs).length} Categories</span>
                
                {/* Data type selector for custom import orgs */}
                {customImportSettings?.enabled && availablePeriodTypes.length > 1 && (
                  <div className="period-type-selector">
                    <label>Data Type:</label>
                    <select 
                      value={selectedPeriodType} 
                      onChange={(e) => setSelectedPeriodType(e.target.value)}
                    >
                      {availablePeriodTypes.map(pt => (
                        <option key={pt} value={pt}>{getPeriodLabel(pt)}</option>
                      ))}
                    </select>
                  </div>
                )}
              </div>

              <div className="kpi-categories">
                {Object.entries(groupedKPIs).map(([category, kpis]) => (
                  <div key={category} className="kpi-category-section">
                    <h3 className="category-title">
                      {category}
                      <span className="category-count-badge">{kpis.length}</span>
                    </h3>
                    
                    <div className="kpi-category">
                      <div className="kpi-grid">
                        {kpis.map((kpi, index) => (
                          <div key={`${category}-${index}`} className="kpi-card">
                            <div className="kpi-header">
                              <h4 className="kpi-name">
                                {kpi.name}
                              </h4>
                              <span className="kpi-value">
                                {typeof kpi.value === 'number' 
                                  ? kpi.value.toLocaleString('en-GB') 
                                  : kpi.value}
                              </span>
                            </div>
                            {kpi.description && kpi.description !== kpi.name && (
                              <p className="kpi-description">{kpi.description}</p>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>

        <div className="kpi-modal-footer">
          <button className="btn-secondary" onClick={onClose}>
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default KPIDetailsModal;
